import React, { useState, useRef, useCallback, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { api, sendMessage, compressImage, uploadMedia, dataUrlToBase64, mediaSrcById, API, uploadFileChunked } from "../api";
import { enqueueMessage } from "../lib/offlineQueue";
import { useApp } from "../context/AppContext";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Progress } from "./ui/progress";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import {
  IssueDialog, MeasurementDialog, MaterialRequestDialog, QuestionDialog, PhotoSendDialog, JobPickerDialog,
} from "./StructuredDialogs";
import { VideoRecorder } from "./VideoRecorder";
import { VideoViewer } from "./VideoViewer";
import { PollCreateDialog } from "./PollCreateDialog";
import { makeVideoPoster } from "../lib/videoPoster";
import { applyPhotoWatermark } from "../lib/watermark";
import { getMic, releaseMic } from "../lib/mic";
import { hapticTick } from "../lib/haptics";
import { Capacitor } from "@capacitor/core";
import { pauseVoiceNote } from "../lib/voicePlayer";
import { playbackFocusAcquire, playbackFocusRelease } from "../lib/wakeword";
import { toast } from "sonner";
import { SenderAvatar } from "./MessageBubble";
import { galleryAvailable, saveToGallery, getSaveToGalleryPref } from "../lib/gallery";
import { StickerTray } from "./StickerTray";
import { nativeVideoPickAvailable, pickNativeVideos } from "../lib/videoCompress";
import { enqueueVideoUpload, enqueueTask } from "../lib/uploadQueue";
import {
  Plus, Send, Mic, Camera, FileText, AlertTriangle, HelpCircle, Ruler, Package, X, MapPin, Briefcase, Lock, Megaphone, AtSign, Video, Image as ImageIcon, Film, ChevronUp, ChevronLeft, Trash2, Square, BarChart3, Pencil, Check, Clock, Zap, Sticker as StickerIcon,
} from "lucide-react";

export function Composer({ conversation, replyContext, clearReply, editingMessage, clearEdit, onEdited, linkedJob, setLinkedJob, linkVisibility, setLinkVisibility }) {
  const { user, t, lang } = useApp();
  // Draft persistence — unsent text survives navigation/app close (WhatsApp-style)
  const draftKey = `osc_draft_${conversation.id}`;
  const [text, setText] = useState(() => {
    try { return localStorage.getItem(`osc_draft_${conversation.id}`) || ""; } catch (e) { return ""; }
  });
  const [dialog, setDialog] = useState(null);
  const [photoDataUrl, setPhotoDataUrl] = useState(null);
  const [photoDataUrls, setPhotoDataUrls] = useState(null); // gallery multi-select
  const [photoFromCamera, setPhotoFromCamera] = useState(false); // camera flow → "+ take another"
  const [photoAppend, setPhotoAppend] = useState(null); // next shot appended to the open dialog
  const [sending, setSending] = useState(false);
  const [recording, setRecording] = useState(false);
  const [recordSecs, setRecordSecs] = useState(0);
  const [recLocked, setRecLocked] = useState(false); // slide-up hands-free lock
  const [recSlideX, setRecSlideX] = useState(0); // finger drag to the LEFT while holding (0..1 → cancel)
  const recStartXRef = useRef(0);
  const [voicePreview, setVoicePreview] = useState(null); // {blob,url,duration} — listen before sending
  const recLockedRef = useRef(false);
  const recStartYRef = useRef(0);
  const recDiscardRef = useRef(false);
  const recPreviewRef = useRef(false);
  // Hold-to-record correctness (quick taps must NEVER keep recording):
  const pressActiveRef = useRef(false);   // finger/mouse currently pressing the mic
  const recordingRef = useRef(false);     // live recording flag (avoids stale-state races)
  const recStartingRef = useRef(false);   // mic warm-up in progress (blocks a 2nd concurrent start)
  const recInterruptedRef = useRef(false); // mic lost / app backgrounded mid-recording
  const [recArming, setRecArming] = useState(false); // finger down, mic warming up
  const reallyStopRecordingRef = useRef(null);
  const [transcribing, setTranscribing] = useState(false);
  const [jobPickerOpen, setJobPickerOpen] = useState(false);
  const [uploadPct, setUploadPct] = useState(null); // null = idle; 0..100 while uploading
  const [recorderOpen, setRecorderOpen] = useState(false);
  const [recordedViewer, setRecordedViewer] = useState(null); // {media, message} — instant review after recording
  const fileRef = useRef(null);
  const galleryRef = useRef(null);
  const docRef = useRef(null);
  const videoRef = useRef(null);
  // Buddy "tira uma foto pro João" → /chat/:id?attach=camera|gallery|video|document
  const [searchParams, setSearchParams] = useSearchParams();
  const [attachIntent, setAttachIntent] = useState(null);
  const attachRefs = { camera: fileRef, gallery: galleryRef, video: videoRef, document: docRef };
  useEffect(() => {
    const a = searchParams.get("attach");
    if (!a || !attachRefs[a]) return;
    setAttachIntent(a);
    const sp = new URLSearchParams(searchParams);
    sp.delete("attach");
    setSearchParams(sp, { replace: true });
    setTimeout(() => attachRefs[a].current?.click(), 400);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const recStartRef = useRef(null);
  const inputRef = useRef(null);
  // Latest reply context for async flows (voice recording callback)
  const replyCtxRef = useRef(replyContext);
  replyCtxRef.current = replyContext;
  // Job groups behave like job conversations (structured items enabled, everything belongs to the job)
  const isJobConv = conversation.type === "job" || !!conversation.job_id;

  // ---- Job info for the photo stamp (date + time + job name burned in) ----
  const [jobInfo, setJobInfo] = useState(null);
  useEffect(() => {
    if (!conversation.job_id) { setJobInfo(null); return; }
    api.get(`/jobs/${conversation.job_id}`).then((r) => setJobInfo(r.data)).catch(() => {});
  }, [conversation.job_id]);
  const stampJob = jobInfo || linkedJob;
  const stampLabel = stampJob ? `JOB #${stampJob.job_number} — ${stampJob.customer_name || ""}`.trim() : "";

  // ---- Quick replies (⚡) — admin-managed 1-tap phrases ----
  const [quickOpen, setQuickOpen] = useState(false);
  const [quickList, setQuickList] = useState(null); // null = not loaded yet
  const openQuickReplies = () => {
    setQuickOpen(true);
    api.get("/quick-replies").then((r) => setQuickList(r.data || [])).catch(() => setQuickList([]));
  };
  const sendQuickReply = async (phrase) => {
    setQuickOpen(false);
    const body = {
      conversation_id: conversation.id, sender_id: user.id, type: "text",
      content: phrase, meta: linkMeta(),
    };
    if (!navigator.onLine) {
      const entry = enqueueMessage({ kind: "text", conversation_id: conversation.id, sender_id: user.id, payload: body, preview: phrase });
      if (entry) toast.info(t("queued_offline"));
      else toast.error(t("queue_full"));
      return;
    }
    try {
      await sendMessage(body);
    } catch (e) {
      if (!e?.response) {
        const entry = enqueueMessage({ kind: "text", conversation_id: conversation.id, sender_id: user.id, payload: body, preview: phrase });
        if (entry) toast.info(t("queued_offline"));
        else toast.error(t("queue_full"));
      } else {
        toast.error("Send failed");
      }
    }
  };

  // ---- Edit mode (WhatsApp-style): prefill the input, ✓ saves, X restores draft ----
  const draftBeforeEditRef = useRef(null);
  useEffect(() => {
    if (editingMessage) {
      draftBeforeEditRef.current = text;
      setText(editingMessage.content || "");
      setTimeout(() => { try { inputRef.current?.focus(); } catch (e) { /* ignore */ } }, 60);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingMessage?.id]);

  const cancelEdit = () => {
    setText(draftBeforeEditRef.current || "");
    draftBeforeEditRef.current = null;
    clearEdit && clearEdit();
  };

  const saveEdit = async () => {
    const content = text.trim();
    if (!content || !editingMessage) return;
    if (content === (editingMessage.content || "").trim()) { cancelEdit(); return; }
    setSending(true);
    try {
      const res = await api.patch(`/messages/${editingMessage.id}`, { user_id: user.id, content });
      onEdited && onEdited(res.data);
      setText(draftBeforeEditRef.current || "");
      draftBeforeEditRef.current = null;
    } catch (e) {
      toast.error(typeof e?.response?.data?.detail === "string" ? e.response.data.detail : t("something_wrong"));
    } finally {
      setSending(false);
    }
  };

  // ---- Scheduled messages ("send later") ----
  const [schedDue, setSchedDue] = useState("");
  const [schedList, setSchedList] = useState([]);
  const refreshScheduled = useCallback(() => {
    api.get("/scheduled-messages", { params: { user_id: user.id, conversation_id: conversation.id } })
      .then((r) => setSchedList(r.data || []))
      .catch(() => {});
  }, [conversation.id, user.id]);
  useEffect(() => { refreshScheduled(); }, [refreshScheduled]);

  const defaultTomorrow6 = () => {
    const d = new Date();
    d.setDate(d.getDate() + 1);
    d.setHours(6, 0, 0, 0);
    const pad = (n) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };

  const scheduleMessage = async () => {
    const content = text.trim();
    if (!content || !schedDue) return;
    const due = new Date(schedDue);
    if (Number.isNaN(due.getTime()) || due <= new Date()) { toast.error(t("schedule_future")); return; }
    try {
      await api.post("/scheduled-messages", {
        conversation_id: conversation.id, sender_id: user.id, type: "text",
        content, meta: linkMeta(), due_at: due.toISOString(),
      });
      setText("");
      setDialog(null);
      refreshScheduled();
      toast.success(t("scheduled_success"));
    } catch (e) {
      toast.error(t("something_wrong"));
    }
  };

  const cancelScheduled = async (sid) => {
    try {
      await api.delete(`/scheduled-messages/${sid}`, { params: { user_id: user.id } });
      refreshScheduled();
      toast.success(t("scheduled_canceled"));
    } catch (e) {
      toast.error(t("something_wrong"));
    }
  };

  // Keep the draft in sync: save while typing, clear when sent/emptied
  useEffect(() => {
    try {
      if (text) localStorage.setItem(draftKey, text);
      else localStorage.removeItem(draftKey);
    } catch (e) { /* storage unavailable — best effort */ }
  }, [text, draftKey]);

  // ---- @Mentions (groups + crew conversations only) ----
  const canMention = conversation.type !== "dm";
  const mentionables = canMention ? (conversation.participants || []).filter((p) => p.id !== user.id) : [];
  const [mentionQuery, setMentionQuery] = useState(null); // null = closed, "" = just typed @
  const [mentionPos, setMentionPos] = useState(0);
  const mentionMatches = mentionQuery === null ? [] : mentionables.filter((p) =>
    p.name.toLowerCase().includes(mentionQuery.toLowerCase())).slice(0, 5);

  // presence pings (throttled): "typing" while you write, "recording" while you hold the mic,
  // "stop" when the text is cleared / the recording ends
  const typingAtRef = useRef(0);
  const presenceKindRef = useRef(null);
  const presenceAllowed = () => !!conversation?.id && conversation.type !== "notes" && conversation.type !== "buddy";
  const pingPresence = (kind) => {
    if (!presenceAllowed()) return;
    const now = Date.now();
    if (kind !== "stop" && kind === presenceKindRef.current && now - typingAtRef.current < 3000) return;
    if (kind === "stop" && !presenceKindRef.current) return;
    typingAtRef.current = now;
    presenceKindRef.current = kind === "stop" ? null : kind;
    api.post(`/conversations/${conversation.id}/typing`, { user_id: user.id, kind }).catch(() => {});
  };
  const pingTyping = () => pingPresence("typing");
  useEffect(() => { if (!text.trim() && presenceKindRef.current === "typing") pingPresence("stop"); }, [text]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!recording) { if (presenceKindRef.current === "recording") pingPresence("stop"); return undefined; }
    pingPresence("recording");
    const iv = setInterval(() => { presenceKindRef.current = null; pingPresence("recording"); }, 3000);
    return () => clearInterval(iv);
  }, [recording]); // eslint-disable-line react-hooks/exhaustive-deps

  const onTextChange = (e) => {
    const val = e.target.value;
    setText(val);
    if (val.trim()) pingTyping();
    if (!canMention) return;
    const pos = e.target.selectionStart ?? val.length;
    const upto = val.slice(0, pos);
    const m = upto.match(/@([^\s@]*)$/);
    if (m) {
      setMentionQuery(m[1]);
      setMentionPos(pos);
    } else {
      setMentionQuery(null);
    }
  };

  const pickMention = (p) => {
    const before = text.slice(0, mentionPos - (mentionQuery?.length || 0) - 1);
    const after = text.slice(mentionPos);
    const inserted = `${before}@${p.name} `;
    setText(`${inserted}${after.replace(/^\s/, "")}`);
    setMentionQuery(null);
    // Restore focus with the caret right after the inserted mention
    const caret = inserted.length;
    setTimeout(() => {
      const el = inputRef.current;
      if (el) {
        el.focus();
        try { el.setSelectionRange(caret, caret); } catch (err) { /* ignore */ }
      }
    }, 0);
  };

  const extractMentions = (content) => {
    if (!canMention) return [];
    return mentionables.filter((p) => content.includes(`@${p.name}`)).map((p) => p.id);
  };

  const linkMeta = () => (linkedJob ? { linked_job_id: linkedJob.id, link_visibility: linkVisibility } : {});

  // Reply payload: merge the quoted message (shortcut back to original) with the
  // highlighted-area crop when the reply came from "Reply to Area"
  const mergeReply = (rc) => {
    if (!rc) return null;
    const r = { ...(rc.quote || {}) };
    if (rc.media_id) r.media_id = rc.media_id;
    if (rc.crop_media_id) r.crop_media_id = rc.crop_media_id;
    return Object.keys(r).length ? r : null;
  };

  const [stickerOpen, setStickerOpen] = useState(false);
  const sendSticker = async (st) => {
    const reply = mergeReply(replyContext);
    if (reply) clearReply && clearReply();
    try {
      await sendMessage({ conversation_id: conversation.id, sender_id: user.id, type: "sticker", content: "", media_ids: [st.id], meta: linkMeta(), reply_to: reply });
    } catch (e) { toast.error(t("something_wrong")); }
  };

  const sendText = async () => {
    const content = text.trim();
    if (!content) return;
    setText("");
    setMentionQuery(null);
    // WhatsApp behavior: keep the keyboard open after sending — refocus the
    // input synchronously (still inside the user gesture, works on iOS).
    try { inputRef.current && inputRef.current.focus(); } catch (e) { /* ignore */ }
    const reply = mergeReply(replyContext);
    clearReply && clearReply();
    const mentionIds = extractMentions(content);
    const body = {
      conversation_id: conversation.id,
      sender_id: user.id,
      type: "text",
      content,
      meta: { ...linkMeta(), ...(mentionIds.length ? { mentions: mentionIds } : {}), ...(urgentArm ? { urgent: true } : {}) },
      reply_to: reply,
    };
    if (urgentArm) setUrgentArm(false);
    const queueIt = () => {
      const entry = enqueueMessage({ kind: "text", conversation_id: conversation.id, sender_id: user.id, payload: body, preview: content });
      if (entry) toast.info(t("queued_offline"));
      else { toast.error(t("queue_full")); setText(content); }
    };
    if (!navigator.onLine) {
      queueIt();
      return;
    }
    try {
      await sendMessage(body);
    } catch (e) {
      if (!e?.response) {
        // Network error — keep it in the offline queue
        queueIt();
      } else {
        toast.error("Send failed");
        setText(content);
      }
    }
  };

  // Shared photo intake — used by the file pickers AND clipboard paste (Ctrl+V)
  const handlePhotoFile = async (file, fromCamera = false) => {
    try {
      const { dataUrl } = await compressImage(file);
      if (fromCamera && dialog === "photo") { setPhotoAppend(dataUrl); return; } // "+ take another"
      setPhotoAppend(null);
      setPhotoDataUrl(dataUrl);
      setPhotoFromCamera(fromCamera);
      setDialog("photo");
    } catch (err) {
      toast.error("Image failed");
    }
  };

  const onPickPhoto = async (e) => {
    const fromCamera = e.target === fileRef.current;
    const files = Array.from(e.target.files || []).slice(0, 20);
    e.target.value = "";
    if (!files.length) return;
    setAttachIntent(null);
    if (files.length === 1) { await handlePhotoFile(files[0], fromCamera); return; }
    // Several photos from the gallery → one dialog, one send
    try {
      const urls = [];
      for (const f of files) urls.push((await compressImage(f)).dataUrl);
      setPhotoDataUrls(urls);
      setPhotoDataUrl(null);
      setPhotoFromCamera(false);
      setPhotoAppend(null);
      setDialog("photo");
    } catch (err) {
      toast.error("Image failed");
    }
  };

  const sendPhoto = async ({ originalDataUrl, editedDataUrl, category, caption, textOverlays, stamp }) => {
    const targetJobId = conversation.job_id || linkedJob?.id || null;
    // Proof-of-work stamp: burn date + time + job name into the image(s)
    // BEFORE upload/queue so the stamp travels with the photo everywhere.
    if (stamp) {
      try {
        const opts = { label: stampLabel, lang };
        originalDataUrl = await applyPhotoWatermark(originalDataUrl, opts);
        if (editedDataUrl) editedDataUrl = await applyPhotoWatermark(editedDataUrl, opts);
      } catch (e) { /* stamp is best-effort — never block the send */ }
    }
    // Photo replies keep the quote/shortcut to the original message
    const reply = mergeReply(replyContext);
    if (reply) clearReply && clearReply();
    const queuePhoto = () => {
      const entry = enqueueMessage({
        kind: "photo",
        conversation_id: conversation.id,
        sender_id: user.id,
        payload: {
          originalDataUrl,
          editedDataUrl: editedDataUrl || null,
          category: category || null,
          caption: caption || "",
          jobId: targetJobId,
          versionLabel: `Markup \u2014 ${user.name.split(" ")[0]}`,
          meta: { ...(category ? { category } : {}), ...linkMeta() },
          reply_to: reply,
          textOverlays: textOverlays || null,
        },
        preview: caption || "",
      });
      if (entry) toast.info(t("queued_offline"));
      else toast.error(t("queue_full"));
    };
    if (!navigator.onLine) {
      queuePhoto();
      return;
    }
    // Online: upload + send run in the BACKGROUND queue (progress strip under the header, toast when sent)
    const meta = { ...(category ? { category } : {}), ...linkMeta() };
    enqueueTask({
      kind: "photo", conversationId: conversation.id, conversationName: conversation.name,
      run: async (onProgress) => {
        try {
          const hasEdited = !!editedDataUrl;
          const original = await uploadMedia({
            base64: dataUrlToBase64(originalDataUrl), jobId: targetJobId, category, uploadedBy: user.id,
            onProgress: (f) => onProgress(f * (hasEdited ? 0.55 : 0.9)),
          });
          let finalId = original.id;
          if (editedDataUrl) {
            const edited = await uploadMedia({
              base64: dataUrlToBase64(editedDataUrl), jobId: targetJobId, category, parentId: original.id,
              versionLabel: `Markup \u2014 ${user.name.split(" ")[0]}`, uploadedBy: user.id, textOverlays: textOverlays || null,
              onProgress: (f) => onProgress(0.55 + f * 0.35),
            });
            finalId = edited.id;
          }
          onProgress(0.95);
          await sendMessage({ conversation_id: conversation.id, sender_id: user.id, type: "photo", content: caption || "", media_ids: [finalId], meta, reply_to: reply });
        } catch (e) {
          if (!e?.response) { queuePhoto(); return; } // offline mid-way → offline queue takes over
          throw e;
        }
      },
    });
  };

  const onPickDoc = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 20);
    e.target.value = "";
    if (!files.length) return;
    setAttachIntent(null);
    if (!navigator.onLine) {
      toast.error(t("offline_banner"));
      return;
    }
    const tooBig = files.filter((f) => f.size > 8 * 1024 * 1024);
    if (tooBig.length) {
      toast.error(`Max 8MB${files.length > 1 ? ` (${tooBig.map((f) => f.name).join(", ")})` : ""}`);
      return;
    }
    setSending(true);
    const n = files.length;
    let failed = 0;
    try {
      for (let i = 0; i < n; i++) {
        const file = files[i];
        const base = i / n;
        try {
          const b64 = await new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result.split(",")[1]);
            reader.onerror = reject;
            reader.readAsDataURL(file);
          });
          setUploadPct(Math.max(2, Math.round(base * 100)));
          const media = await uploadMedia({
            base64: b64, contentType: file.type || "application/octet-stream", kind: "document",
            jobId: conversation.job_id || linkedJob?.id || null, uploadedBy: user.id, filename: file.name,
            onProgress: (f) => setUploadPct(Math.round((base + (f * 0.92) / n) * 100)),
          });
          await sendMessage({
            conversation_id: conversation.id, sender_id: user.id, type: "document",
            content: file.name, media_ids: [media.id], meta: linkMeta(),
          });
        } catch (err) {
          failed += 1;
        }
      }
      setUploadPct(100);
      if (failed) toast.error(n > 1 ? `${failed}/${n} — Document failed` : "Document failed");
    } finally {
      setSending(false);
      setTimeout(() => setUploadPct(null), 400);
    }
  };

  // ---- Video upload (streamed multipart -> GridFS, up to 400MB) ----
  const onPickVideo = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 10);
    e.target.value = "";
    await sendVideoFiles(files);
  };

  // Native app: gallery picker → BACKGROUND queue (compress 720p → chunked upload → message).
  // The user can leave the chat; a progress strip follows them and a toast confirms "Video sent to …".
  const [qualityAsk, setQualityAsk] = useState(null); // picked native videos waiting for "Fast / Original"
  const pickVideoNative = async () => {
    let picked = [];
    try { picked = await pickNativeVideos(10); } catch (e) { videoRef.current?.click(); return; }
    if (!picked.length) return;
    setAttachIntent(null);
    setQualityAsk(picked);
  };
  const sendNativeVideos = (picked, quality) => {
    setQualityAsk(null);
    const reply = mergeReply(replyContext);
    if (reply) clearReply && clearReply();
    picked.forEach((pk, i) => enqueueVideoUpload({
      conversationId: conversation.id, conversationName: conversation.name, senderId: user.id,
      jobId: conversation.job_id || linkedJob?.id || null, picked: pk, meta: linkMeta(), replyTo: i === 0 ? reply : null, quality,
    }));
    toast.info(t("video_sending_bg"));
  };

  const sendVideoFiles = async (files) => {
    if (!files.length) return;
    setAttachIntent(null);
    if (!navigator.onLine) { toast.error(t("offline_banner")); return; }
    const reply = mergeReply(replyContext);
    if (reply) clearReply && clearReply();
    files.forEach((file, i) => enqueueVideoUpload({
      conversationId: conversation.id, conversationName: conversation.name, senderId: user.id,
      jobId: conversation.job_id || linkedJob?.id || null, source: file, meta: linkMeta(), replyTo: i === 0 ? reply : null,
    }));
    toast.info(t("video_sending_bg"));
  };

  // ---- In-app recorded video: instant review + send-on-demand ----
  // Stop recording -> player opens IMMEDIATELY with the local blob.
  // Upload starts right away in the background (so "Send" feels instant),
  // but the message is only sent when the user taps "Send" in the player.
  // Closing (X) without sending discards everything (aborts/deletes the upload).
  const recordedRef = useRef(null); // {media, uploadPromise, abort, discarded, audioBlob}

  const startVideoReview = async (blob, mimeType, filename, audioBlob, expectCaps) => {
    if (blob.size > 400 * 1024 * 1024) {
      toast.error("Max 400MB");
      return;
    }
    const blobUrl = URL.createObjectURL(blob);
    const abort = new AbortController();
    const rec = { media: null, uploadPromise: null, abort, discarded: false, audioBlob };
    recordedRef.current = rec;
    // 1) Instant review: player opens right away (markup + captions + Send button)
    setRecordedViewer({ media: { id: `local-${Date.now()}`, url: blobUrl, filename }, message: null, canSend: true, expectCaptions: expectCaps });
    if (!navigator.onLine) {
      toast.error(t("offline_banner"));
      return;
    }
    setUploadPct(1);
    const posterPromise = makeVideoPoster(blob); // thumbnail for the chat bubble
    // 2) Background upload (streamed multipart -> GridFS) — no message yet
    rec.uploadPromise = (async () => {
      const jid = conversation.job_id || linkedJob?.id;
      const res = { data: await uploadFileChunked({
        file: new File([blob], filename, { type: mimeType || "video/webm" }), kind: "video", jobId: jid || null, uploadedBy: user.id,
        signal: abort.signal,
        onProgress: (p) => setUploadPct(Math.min(96, Math.round(p * 96))),
      }) };
      rec.media = res.data;
      setUploadPct(100);
      setTimeout(() => setUploadPct(null), 600);
      try {
        const poster = await posterPromise;
        if (poster && !rec.discarded) await api.post(`/media/${res.data.id}/poster`, { data_url: poster });
      } catch (err) { /* poster is best-effort */ }
      // 3) Auto captions (Whisper + translation) from the tiny parallel audio track
      if (audioBlob && audioBlob.size > 200 && !rec.discarded) {
        const aExt = (audioBlob.type || "").includes("mp4") ? "m4a" : "webm";
        const cfd = new FormData();
        cfd.append("file", new File([audioBlob], `captions.${aExt}`, { type: audioBlob.type || "audio/webm" }), `captions.${aExt}`);
        api.post(`/media/${res.data.id}/captions`, cfd, { headers: { "Content-Type": "multipart/form-data" } }).catch(() => {});
      }
      // 4) Adopt the server media id (viewer starts polling captions) — keep blob playing
      setRecordedViewer((prev) => (prev && !rec.discarded ? { ...prev, media: { ...res.data, url: prev.media.url } } : prev));
      return res.data;
    })();
    rec.uploadPromise.catch(() => {
      setUploadPct(null);
      if (!rec.discarded) toast.error("Video failed");
    });
  };

  const onRecordedVideo = async (blob, mimeType, duration, audioBlob) => {
    setRecorderOpen(false);
    const ext = (mimeType || "").includes("mp4") ? "mp4" : "webm";
    await startVideoReview(blob, mimeType, `video-${Date.now()}.${ext}`, audioBlob, true);
  };

  // Pasted/dropped video files reuse the same review flow — upload starts right away,
  // but the message is only sent when the user confirms in the player (no auto-captions)
  const handleVideoFile = async (file) => {
    // iOS gallery picks arrive as .MOV, often with an EMPTY file.type — infer from the name
    const nameExt = ((file.name || "").split(".").pop() || "").toLowerCase();
    const byExt = { mov: "video/quicktime", qt: "video/quicktime", mp4: "video/mp4", m4v: "video/mp4", webm: "video/webm", "3gp": "video/3gpp" }[nameExt];
    const mime = byExt || file.type || "video/mp4";
    const ext = nameExt || (mime.includes("quicktime") ? "mov" : mime.includes("mp4") ? "mp4" : "webm");
    const filename = file.name || `video-${Date.now()}.${ext}`;
    await startVideoReview(file, mime, filename, null, false);
  };

  const sendRecordedVideo = async () => {
    const rec = recordedRef.current;
    const viewer = recordedViewer;
    if (!rec) return;
    recordedRef.current = null;
    // Close the player; sending completes in the background (progress bar in composer)
    try { if (viewer?.media?.url?.startsWith("blob:")) URL.revokeObjectURL(viewer.media.url); } catch (e) { /* ignore */ }
    setRecordedViewer(null);
    const reply = mergeReply(replyContext);
    if (reply) clearReply && clearReply();
    setSending(true);
    try {
      const media = rec.media || (await rec.uploadPromise);
      await sendMessage({
        conversation_id: conversation.id, sender_id: user.id, type: "video",
        content: "", media_ids: [media.id], meta: linkMeta(), reply_to: reply,
      });
      if (getSaveToGalleryPref() && galleryAvailable()) {
        const n = await saveToGallery([{ path: mediaSrcById(media.id), kind: "video" }], stampJob);
        if (n > 0) toast.success(t("saved_to_gallery_n").replace("{n}", n));
        else toast.error(t("gallery_save_failed"));
      }
    } catch (err) {
      toast.error("Video failed");
    } finally {
      setSending(false);
    }
  };

  const discardRecordedVideo = () => {
    const rec = recordedRef.current;
    const viewer = recordedViewer;
    recordedRef.current = null;
    try { if (viewer?.media?.url?.startsWith("blob:")) URL.revokeObjectURL(viewer.media.url); } catch (e) { /* ignore */ }
    setRecordedViewer(null);
    setUploadPct(null);
    if (!rec) return;
    rec.discarded = true;
    toast.info(t("video_discarded"));
    try { rec.abort.abort(); } catch (e) { /* ignore */ }
    // Delete the media if the upload had already completed
    (async () => {
      try {
        const media = rec.media || (rec.uploadPromise ? await rec.uploadPromise.catch(() => null) : null);
        if (media?.id) await api.delete(`/media/${media.id}`);
      } catch (e) { /* orphan cleanup is best-effort */ }
    })();
  };

  // ---- Voice recording (hold) ----
  // iOS/WKWebView claims webm support on newer versions but produces EMPTY blobs (0 bytes) —
  // production logs: "voice upload rejected (undecodable, ct=audio/webm, 0B)". Apple → AAC/mp4 first.
  const pickRecorderMime = () => {
    const ua = navigator.userAgent || "";
    const apple = Capacitor.getPlatform() === "ios" || (/iPhone|iPad|iPod|Macintosh/.test(ua) && /Safari/.test(ua) && !/Chrome|CriOS|Android/.test(ua));
    const order = apple
      ? ["audio/mp4", "audio/mp4;codecs=mp4a.40.2", "audio/aac", "audio/webm;codecs=opus", "audio/webm"]
      : ["audio/webm;codecs=opus", "audio/webm", "audio/mp4", "audio/ogg;codecs=opus"];
    for (const m of order) { try { if (MediaRecorder.isTypeSupported(m)) return m; } catch (e) { /* ignore */ } }
    return "";
  };

  const uploadVoice = useCallback(async (blob, duration) => {
    setTranscribing(true);
    try {
      const fd = new FormData();
      const ext = /mp4|m4a|aac/.test(blob.type || "") ? "m4a" : /ogg/.test(blob.type || "") ? "ogg" : "webm";
      fd.append("file", blob, `voice.${ext}`);
      fd.append("conversation_id", conversation.id);
      fd.append("sender_id", user.id);
      fd.append("duration", String(Math.round(duration)));
      const jid = conversation.job_id || linkedJob?.id;
      if (jid) fd.append("job_id", jid);
      // Voice in a DM with active Job Topic Mode also gets the job badge
      if (!conversation.job_id && linkedJob?.id) fd.append("linked_job_id", linkedJob.id);
      // Voice replies keep the quote/shortcut to the original message (incl. area crops)
      const rq = mergeReply(replyCtxRef.current);
      if (rq) {
        fd.append("reply_to", JSON.stringify(rq));
        clearReply && clearReply();
      }
      const res = await fetch(`${API}/voice`, { method: "POST", body: fd });
      if (res.ok) hapticTick("send");
      if (!res.ok) {
        // 422 = the server could not decode the audio (recording interrupted/corrupt) — never deliver a broken note
        let detail = "";
        try { detail = (await res.json())?.detail || ""; } catch (e) { /* ignore */ }
        toast.error(detail === "audio_empty" ? t("voice_empty") : res.status === 422 ? t("voice_invalid") : t("voice_send_failed"));
      }
    } catch (err) {
      toast.error(t("voice_send_failed"));
    } finally {
      setTranscribing(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversation.id, conversation.job_id, linkedJob, user.id, clearReply]);

  const startRecording = useCallback(async (e) => {
    e.preventDefault();
    if (recording || recordingRef.current || recStartingRef.current) return;
    // WhatsApp behavior: recording only runs WHILE HOLDING (or after sliding
    // up to lock). pressActiveRef tracks the finger — if it lifts during the
    // async mic warm-up (quick tap), we do NOT start recording at all.
    pressActiveRef.current = true;
    recStartingRef.current = true; // one recorder at a time, even if touch + mouse both fire
    setRecArming(true); // instant feedback while the mic warms up (can take ~1s on iOS)
    pauseVoiceNote(); // iOS: an <audio> playing while the mic opens can leave the recorder with 0 bytes
    try {
      const stream = await getMic(); // shared warm stream = instant start
      if (!pressActiveRef.current) {
        // Finger already lifted → quick tap. Don't record; show the hint.
        releaseMic(60000);
        toast.info(t("hold_to_record"));
        return;
      }
      const mime = pickRecorderMime();
      const mr = new MediaRecorder(stream, mime ? { mimeType: mime } : undefined);
      chunksRef.current = [];
      recInterruptedRef.current = false;
      mr.ondataavailable = (ev) => { if (ev.data.size > 0) chunksRef.current.push(ev.data); };
      mr.onerror = () => { recInterruptedRef.current = true; reallyStopRecordingRef.current && reallyStopRecordingRef.current(); };
      // Mic taken away mid-recording (phone call, Siri, screen lock, another app) → close the file
      // NOW while it is still valid instead of sending a truncated/corrupt note
      stream.getAudioTracks().forEach((tk) => { tk.onended = () => { recInterruptedRef.current = true; reallyStopRecordingRef.current && reallyStopRecordingRef.current(); }; });
      mr.onstop = async () => {
        releaseMic(60000); // keep warm 60s so the next voice note starts instantly
        if (recDiscardRef.current) { recDiscardRef.current = false; return; } // cancelled while locked
        const duration = (Date.now() - recStartRef.current) / 1000;
        if (duration < 0.6) {
          // Too short (tap-like) → discard + WhatsApp-style hint
          recPreviewRef.current = false;
          toast.info(t("hold_to_record"));
          return;
        }
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        if (blob.size < 200) {
          recPreviewRef.current = false;
          toast.error(t("voice_invalid"));
          return;
        }
        if (recPreviewRef.current || recInterruptedRef.current) {
          // Locked "stop" (or interrupted recording) -> let the user LISTEN before deciding to send/discard
          recPreviewRef.current = false;
          if (recInterruptedRef.current) toast.info(t("voice_interrupted"));
          setVoicePreview({ blob, url: URL.createObjectURL(blob), duration });
          return;
        }
        await uploadVoice(blob, duration);
      };
      mediaRecorderRef.current = mr;
      recStartRef.current = Date.now();
      recStartYRef.current = e.touches?.[0]?.clientY ?? e.clientY ?? 0;
      recStartXRef.current = e.touches?.[0]?.clientX ?? e.clientX ?? 0;
      setRecSlideX(0);
      recLockedRef.current = false;
      recDiscardRef.current = false;
      setRecLocked(false);
      mr.start();
      recordingRef.current = true;
      setRecording(true);
      setRecArming(false);
      setRecordSecs(0);
      hapticTick("start"); // WhatsApp-style: the phone confirms the mic is ON
      // Wall-clock based: a throttled/suspended tab fires the missed ticks in a burst, which made
      // the counter "race"; deriving from the start time keeps it exact
      timerRef.current = setInterval(() => setRecordSecs(Math.floor((Date.now() - recStartRef.current) / 1000)), 500);
      // Double-check: if the finger lifted between the guard above and
      // mr.start() (extreme quick tap), stop right away.
      if (!pressActiveRef.current && !recLockedRef.current) {
        reallyStopRecordingRef.current && reallyStopRecordingRef.current();
      }
    } catch (err) {
      toast.error("Microphone not available");
    } finally {
      recStartingRef.current = false;
      setRecArming(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording, uploadVoice, t]);

  const reallyStopRecording = useCallback(() => {
    recordingRef.current = false;
    setRecording(false);
    setRecLocked(false);
    recLockedRef.current = false;
    clearInterval(timerRef.current);
    try { mediaRecorderRef.current?.stop(); } catch (err) { /* ignore */ }
  }, []);
  reallyStopRecordingRef.current = reallyStopRecording;

  const stopRecording = useCallback((e) => {
    e && e.preventDefault();
    pressActiveRef.current = false; // finger lifted — quick taps never record
    if (!recordingRef.current) return;
    if (recLockedRef.current) return; // locked: lifting the finger keeps recording
    reallyStopRecording();
  }, [reallyStopRecording]);

  // While holding: slide UP -> lock hands-free · slide LEFT -> cancel (WhatsApp-style)
  const CANCEL_PX = 90;
  const onRecordMove = useCallback((e) => {
    if (!recording || recLockedRef.current) return;
    const y = e.touches?.[0]?.clientY ?? e.clientY;
    const x = e.touches?.[0]?.clientX ?? e.clientX;
    if (y == null || x == null) return;
    const dx = recStartXRef.current - x; // how far LEFT
    const dy = recStartYRef.current - y; // how far UP
    if (dx > 25 && dx > dy) {
      const p = Math.min(dx / CANCEL_PX, 1);
      setRecSlideX(p);
      if (p >= 1) {
        recDiscardRef.current = true;
        hapticTick("cancel");
        pressActiveRef.current = false;
        setRecSlideX(0);
        try { navigator.vibrate && navigator.vibrate([20, 40, 20]); } catch (err) { /* ignore */ }
        reallyStopRecordingRef.current && reallyStopRecordingRef.current();
        toast.info(t("voice_cancelled"));
      }
      return;
    }
    setRecSlideX(0);
    if (dy > 55) {
      recLockedRef.current = true;
      setRecLocked(true);
      hapticTick("lock");
      try { navigator.vibrate && navigator.vibrate(30); } catch (err) { /* ignore */ }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [recording]);

  // Trash while locked: stop WITHOUT sending
  const cancelRecording = useCallback(() => {
    recDiscardRef.current = true;
    reallyStopRecording();
  }, [reallyStopRecording]);

  // Locked "stop" button -> stop recording and LISTEN before sending
  const stopToPreview = useCallback(() => {
    recPreviewRef.current = true;
    reallyStopRecording();
  }, [reallyStopRecording]);

  // Listening to the preview is media PLAYBACK: hold the iOS playback focus
  // (recognizer off) for as long as the preview bar is visible
  useEffect(() => {
    if (!voicePreview) return undefined;
    let held = false;
    let alive = true;
    playbackFocusAcquire().then(() => { if (alive) held = true; else playbackFocusRelease(); });
    return () => { alive = false; if (held) playbackFocusRelease(); };
  }, [voicePreview]);

  const discardPreview = useCallback(() => {
    if (voicePreview?.url) { try { URL.revokeObjectURL(voicePreview.url); } catch (e) { /* ignore */ } }
    setVoicePreview(null);
  }, [voicePreview]);

  const sendPreview = useCallback(async () => {
    if (!voicePreview) return;
    const { blob, url, duration } = voicePreview;
    setVoicePreview(null);
    try { URL.revokeObjectURL(url); } catch (e) { /* ignore */ }
    await uploadVoice(blob, duration);
  }, [voicePreview, uploadVoice]);

  // Desktop: while recording, track the mouse on the whole window so dragging
  // up locks even after the cursor leaves the small mic button
  useEffect(() => {
    if (!recording) return undefined;
    const mv = (e) => onRecordMove(e);
    const up = (e) => stopRecording(e);
    // App sent to background / screen locked while recording: iOS suspends the WebView and the
    // mic, leaving a truncated file and a timer that "races" to catch up on return. Close the
    // recording cleanly now; the user listens to what was captured and decides to send.
    const onHide = () => {
      if (document.visibilityState !== "hidden" || !recordingRef.current) return;
      recInterruptedRef.current = true;
      reallyStopRecordingRef.current && reallyStopRecordingRef.current();
    };
    window.addEventListener("mousemove", mv);
    window.addEventListener("mouseup", up);
    document.addEventListener("visibilitychange", onHide);
    return () => {
      window.removeEventListener("mousemove", mv);
      window.removeEventListener("mouseup", up);
      document.removeEventListener("visibilitychange", onHide);
    };
  }, [recording, onRecordMove, stopRecording]);

  // ---- Clipboard paste (Ctrl+V) — WhatsApp Web style ----
  // Pasting an image opens the photo preview dialog; pasting a video opens the
  // review player (background upload + send-on-demand). Works window-wide so the
  // textarea doesn't need focus, but never hijacks other editable fields/dialogs.
  const pasteRef = useRef(null);
  pasteRef.current = (e) => {
    const files = Array.from(e.clipboardData?.files || []);
    if (!files.length) return; // plain text paste — let the browser handle it
    if (dialog || recorderOpen || recordedViewer || voicePreview || recording || jobPickerOpen) return;
    const el = e.target;
    const isNode = typeof Node !== "undefined" && el instanceof Node;
    const isOurInput = isNode && inputRef.current && (el === inputRef.current || (inputRef.current.contains && inputRef.current.contains(el)));
    const isEditable = isNode && (el.tagName === "INPUT" || el.tagName === "TEXTAREA" || el.isContentEditable);
    if (isEditable && !isOurInput) return; // pasting into search boxes etc.
    const image = files.find((f) => (f.type || "").startsWith("image/"));
    const video = files.find((f) => (f.type || "").startsWith("video/"));
    if (!image && !video) return;
    e.preventDefault();
    if (image) handlePhotoFile(image);
    else handleVideoFile(video);
  };
  useEffect(() => {
    const fn = (e) => pasteRef.current && pasteRef.current(e);
    window.addEventListener("paste", fn);
    return () => window.removeEventListener("paste", fn);
  }, []);

  // ---- Drag & Drop — same flow as clipboard paste ----
  // Dragging a file anywhere over the conversation shows a drop overlay.
  // Dropped images open the photo editor; dropped videos open the review player.
  const [dragOver, setDragOver] = useState(false);
  const dragDepthRef = useRef(0);
  const dropRef = useRef(null);
  dropRef.current = {
    blocked: !!(dialog || recorderOpen || recordedViewer || voicePreview || recording || jobPickerOpen),
    handle: (files) => {
      const image = files.find((f) => (f.type || "").startsWith("image/"));
      const video = files.find((f) => (f.type || "").startsWith("video/"));
      if (image) handlePhotoFile(image);
      else if (video) handleVideoFile(video);
      else toast.error(t("drop_unsupported"));
    },
  };
  useEffect(() => {
    const hasFiles = (e) => Array.from(e.dataTransfer?.types || []).includes("Files");
    const onDragEnter = (e) => {
      if (!hasFiles(e) || dropRef.current.blocked) return;
      e.preventDefault();
      dragDepthRef.current += 1;
      setDragOver(true);
    };
    const onDragOver = (e) => {
      if (!hasFiles(e) || dropRef.current.blocked) return;
      e.preventDefault(); // required so the browser allows the drop
    };
    const onDragLeave = (e) => {
      if (!hasFiles(e)) return;
      dragDepthRef.current = Math.max(0, dragDepthRef.current - 1);
      if (dragDepthRef.current === 0) setDragOver(false);
    };
    const onDrop = (e) => {
      dragDepthRef.current = 0;
      setDragOver(false);
      if (!hasFiles(e) || dropRef.current.blocked) return;
      e.preventDefault();
      const files = Array.from(e.dataTransfer?.files || []);
      if (files.length) dropRef.current.handle(files);
    };
    window.addEventListener("dragenter", onDragEnter);
    window.addEventListener("dragover", onDragOver);
    window.addEventListener("dragleave", onDragLeave);
    window.addEventListener("drop", onDrop);
    return () => {
      window.removeEventListener("dragenter", onDragEnter);
      window.removeEventListener("dragover", onDragOver);
      window.removeEventListener("dragleave", onDragLeave);
      window.removeEventListener("drop", onDrop);
    };
  }, []);

  // 🚨 Urgent mode (DMs): arms the NEXT text message — backend re-pushes the
  // recipient every 2 min (max 3x) and alerts the sender if never read.
  const [urgentArm, setUrgentArm] = useState(false);

  const plusItems = [
    ...(conversation.type === "dm" ? [{
      key: "urgent",
      label: t("urgent_menu"),
      icon: AlertTriangle,
      danger: true,
      action: () => setUrgentArm(true),
    }] : []),
    { key: "take-photo", label: t("take_photo"), icon: Camera, action: () => fileRef.current?.click() },
    { key: "photo", label: t("photo_gallery"), icon: ImageIcon, action: () => galleryRef.current?.click() },
    { key: "record-video", label: t("record_video"), icon: Video, action: () => setRecorderOpen(true) },
    { key: "video", label: t("video_gallery"), icon: Film, action: () => (nativeVideoPickAvailable() ? pickVideoNative() : videoRef.current?.click()) },
    { key: "document", label: t("document"), icon: FileText, action: () => docRef.current?.click() },
    {
      key: "schedule",
      label: t("schedule_message"),
      icon: Clock,
      action: () => {
        if (!text.trim()) { toast.info(t("schedule_needs_text")); return; }
        setSchedDue(defaultTomorrow6());
        setDialog("schedule");
      },
    },
    ...(conversation.type !== "dm" ? [{ key: "poll", label: t("poll"), icon: BarChart3, action: () => setDialog("poll-create") }] : []),
    ...(!isJobConv ? [{ key: "link-job", label: t("link_to_job"), icon: Briefcase, action: () => setJobPickerOpen(true) }] : []),
    ...(isJobConv || linkedJob ? [
      { key: "issue", label: t("issue"), icon: AlertTriangle, action: () => setDialog("issue"), danger: true },
      { key: "question", label: t("question"), icon: HelpCircle, action: () => setDialog("question") },
      { key: "measurement", label: t("measurement"), icon: Ruler, action: () => setDialog("measurement") },
      { key: "material", label: t("material_request"), icon: Package, action: () => setDialog("material") },
    ] : [
      { key: "question", label: t("question"), icon: HelpCircle, action: () => setDialog("question") },
      // measurements also live in DMs (e.g. telling the supervisor about a wrong-sized window):
      // the wizard auto-detects today's scheduled job for the PDF flow
      { key: "measurement", label: t("measurement"), icon: Ruler, action: () => setDialog("measurement") },
    ]),
  ];

  return (
    <div className="border-t bg-background px-3 py-2 sticky bottom-0" data-keep-keyboard="1" data-testid="composer-root">
      {/* Drag & drop overlay — pointer-events-none so the drop still reaches the window listener */}
      {dragOver && (
        <div className="fixed inset-0 z-[60] bg-background/80 backdrop-blur-sm flex items-center justify-center pointer-events-none" data-testid="drop-media-overlay">
          <div className="border-2 border-dashed border-primary rounded-2xl px-10 py-8 flex flex-col items-center gap-3 bg-card/95 shadow-xl mx-4">
            <div className="flex items-center gap-3 text-primary">
              <ImageIcon className="h-9 w-9" />
              <Film className="h-9 w-9" />
            </div>
            <div className="text-base font-semibold" data-testid="drop-media-title">{t("drop_media_here")}</div>
            <div className="text-xs text-muted-foreground text-center">{t("drop_media_sub")}</div>
          </div>
        </div>
      )}

      {/* Persistent job topic bar */}
      {linkedJob && (
        <div className="flex items-center gap-2 bg-primary/10 border border-primary/25 rounded-lg p-2 mb-2" data-testid="linked-job-bar">
          <Briefcase className="h-4 w-4 text-primary shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-xs truncate">
              <span className="font-mono-plex font-semibold text-primary">#{linkedJob.job_number}</span> — {linkedJob.customer_name}
            </div>
            <div className="text-[10px] text-muted-foreground">{t("job_topic_active")}</div>
          </div>
          <button
            data-testid="link-visibility-toggle"
            onClick={() => setLinkVisibility(linkVisibility === "private" ? "shared" : "private")}
            className={`flex items-center gap-1 text-[10px] font-medium rounded-full border px-2 py-1 transition-colors ${linkVisibility === "shared" ? "border-[hsl(var(--success))] text-[hsl(var(--success))]" : "border-muted-foreground/40 text-muted-foreground"}`}
            title={linkVisibility === "private" ? t("private") : t("shared")}
          >
            {linkVisibility === "shared" ? <Megaphone className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
            {linkVisibility === "shared" ? t("shared") : t("private")}
          </button>
          <button onClick={() => setLinkedJob(null)} data-testid="end-job-topic" title={t("end_job_topic")} className="h-7 w-7 flex items-center justify-center rounded-full hover:bg-background border">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Edit mode bar */}
      {editingMessage && (
        <div className="flex items-center gap-2 bg-muted rounded-lg p-2 mb-2 border-l-2 border-primary" data-testid="edit-context-bar">
          <Pencil className="h-4 w-4 text-primary shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-primary">{t("editing_message")}</div>
            <div className="text-xs text-muted-foreground truncate">{editingMessage.content}</div>
          </div>
          <button data-testid="edit-context-clear" onClick={cancelEdit} className="h-7 w-7 flex items-center justify-center rounded hover:bg-background shrink-0">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* 🚨 Urgent armed bar */}
      {urgentArm && !editingMessage && (
        <div className="flex items-center gap-2 bg-destructive/10 rounded-lg p-2 mb-2 border-l-2 border-destructive" data-testid="urgent-armed-bar">
          <AlertTriangle className="h-4 w-4 text-destructive shrink-0" />
          <div className="flex-1 min-w-0 text-xs font-semibold text-destructive truncate">
            {t("urgent_armed")}
          </div>
          <button data-testid="urgent-armed-clear" onClick={() => setUrgentArm(false)} className="h-7 w-7 flex items-center justify-center rounded hover:bg-background shrink-0">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Scheduled messages chip */}
      {schedList.length > 0 && !editingMessage && (
        <button
          data-testid="scheduled-chip"
          onClick={() => setDialog("scheduled-list")}
          className="flex items-center gap-1.5 text-xs font-medium text-primary bg-primary/10 hover:bg-primary/15 rounded-full px-3 py-1.5 mb-2 transition-colors"
        >
          <Clock className="h-3.5 w-3.5" /> {t("scheduled_chip").replace("{n}", String(schedList.length))}
        </button>
      )}

      {/* Reply context bar */}
      {replyContext && replyContext.quote && (
        <div className="flex items-center gap-2 bg-muted rounded-lg p-2 mb-2 border-l-2 border-primary" data-testid="reply-context-bar">
          {replyContext.cropDataUrl || (replyContext.quote.media_id && (!replyContext.quote.type || replyContext.quote.type === "photo")) ? (
            <img
              src={replyContext.cropDataUrl || mediaSrcById(replyContext.quote.media_id)}
              alt=""
              className="h-10 w-10 object-cover rounded shrink-0"
            />
          ) : (
            <span className="relative shrink-0">
              <SenderAvatar
                sender={(conversation?.participants || []).find((p) => p.id === replyContext.quote.sender_id) || { name: replyContext.quote.sender_name }}
                className="h-9 w-9"
              />
              {replyContext.quote.media_id && (
                <span className="absolute -bottom-0.5 -right-0.5 h-4 w-4 rounded-full bg-primary text-primary-foreground flex items-center justify-center ring-2 ring-card">
                  {replyContext.quote.type === "voice" ? <Mic className="h-2.5 w-2.5" /> : replyContext.quote.type === "video" ? <Film className="h-2.5 w-2.5" /> : <FileText className="h-2.5 w-2.5" />}
                </span>
              )}
            </span>
          )}
          <div className="flex-1 min-w-0">
            <div className="text-[11px] font-semibold text-primary truncate">
              {t("replying_to")} {replyContext.quote.sender_name}
            </div>
            <div className="text-xs text-muted-foreground truncate flex items-center gap-1">
              {replyContext.crop_media_id && <MapPin className="h-3 w-3 shrink-0" />}
              {replyContext.quote.content || (replyContext.crop_media_id ? t("area_highlighted") : "")}
            </div>
          </div>
          <button onClick={clearReply} data-testid="reply-context-clear" className="h-6 w-6 flex items-center justify-center rounded hover:bg-background shrink-0">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}
      {replyContext && !replyContext.quote && (
        <div className="flex items-center gap-2 bg-muted rounded-lg p-2 mb-2" data-testid="reply-context-bar">
          {replyContext.cropDataUrl && <img src={replyContext.cropDataUrl} alt="" className="h-10 w-10 object-cover rounded" />}
          <span className="text-xs text-muted-foreground flex items-center gap-1 flex-1">
            <MapPin className="h-3 w-3" /> {t("area_highlighted")}
          </span>
          <button onClick={clearReply} data-testid="reply-context-clear" className="h-6 w-6 flex items-center justify-center rounded hover:bg-background">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {transcribing && (
        <div className="text-xs text-primary flex items-center gap-2 mb-1.5 px-1">
          <Mic className="h-3.5 w-3.5 recording-pulse" /> {t("transcribing")}
        </div>
      )}

      {/* Upload progress (photos/documents) */}
      {uploadPct !== null && (
        <div className="flex items-center gap-2 mb-2 px-1" data-testid="upload-progress-bar">
          <Camera className="h-3.5 w-3.5 text-primary shrink-0" />
          <Progress value={uploadPct} className="h-1.5 flex-1" />
          <span className="text-[11px] font-medium text-primary tabular-nums shrink-0" data-testid="upload-progress-pct">
            {t("uploading")} {uploadPct}%
          </span>
        </div>
      )}

      {/* @Mention autocomplete (groups + crews) */}
      {mentionQuery !== null && mentionMatches.length > 0 && (
        <div className="bg-card border rounded-xl shadow-lg mb-2 overflow-hidden" data-testid="mention-autocomplete">
          <div className="px-3 py-1.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground border-b flex items-center gap-1">
            <AtSign className="h-3 w-3" /> {t("mention_someone")}
          </div>
          {mentionMatches.map((p) => (
            <button
              key={p.id}
              data-testid={`mention-option-${p.id}`}
              onClick={() => pickMention(p)}
              className="w-full flex items-center gap-2.5 px-3 py-2 text-left hover:bg-muted transition-colors"
            >
              <span className="h-7 w-7 rounded-full bg-primary/10 text-primary text-[11px] font-bold flex items-center justify-center shrink-0">
                {p.name.split(" ").map((w) => w[0]).slice(0, 2).join("")}
              </span>
              <span className="min-w-0">
                <span className="block text-sm font-medium truncate">{p.name}</span>
                <span className="block text-[10px] text-muted-foreground capitalize">{t(p.role)}</span>
              </span>
            </button>
          ))}
        </div>
      )}

      {attachIntent && (
        <div className="mb-2 flex items-center gap-2 rounded-2xl border border-primary/30 bg-primary/5 px-3 py-2" data-testid="attach-intent-bar">
          <button
            type="button"
            className="flex flex-1 items-center gap-2 text-left text-sm font-medium text-primary active:opacity-70"
            onClick={() => attachRefs[attachIntent].current?.click()}
            data-testid="attach-intent-open"
          >
            {attachIntent === "camera" && <Camera className="h-4 w-4" />}
            {attachIntent === "gallery" && <ImageIcon className="h-4 w-4" />}
            {attachIntent === "video" && <Film className="h-4 w-4" />}
            {attachIntent === "document" && <FileText className="h-4 w-4" />}
            <span>{t(`attach_intent_${attachIntent}`)}</span>
          </button>
          <button type="button" className="p-1 text-muted-foreground" onClick={() => setAttachIntent(null)} aria-label="close" data-testid="attach-intent-close">
            <X className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Hidden pickers — always mounted */}
      <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={onPickPhoto} />
      <input ref={galleryRef} type="file" accept="image/*" multiple className="hidden" onChange={onPickPhoto} data-testid="composer-gallery-input" />
      <input ref={docRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,.csv" multiple className="hidden" onChange={onPickDoc} data-testid="composer-doc-input" />
      <input ref={videoRef} type="file" accept="video/*" multiple className="hidden" onChange={onPickVideo} data-testid="composer-video-input" />

      {voicePreview ? (
        /* ===== VOICE PREVIEW — 2 lines: player on top, tools below ===== */
        <div className="space-y-2" data-testid="voice-preview-bar">
          <audio
            controls
            src={voicePreview.url}
            data-testid="voice-preview-player"
            className="h-11 w-full"
            onLoadedMetadata={(e) => {
              // Chrome quirk: MediaRecorder blobs report Infinity until seeked
              const a = e.target;
              if (a.duration === Infinity) {
                a.currentTime = 1e7;
                a.ontimeupdate = () => { a.ontimeupdate = null; a.currentTime = 0; };
              }
            }}
          />
          <div className="flex items-center justify-between px-1">
            <button
              type="button"
              data-testid="voice-preview-discard"
              onClick={discardPreview}
              className="h-12 w-12 flex items-center justify-center rounded-xl border border-destructive/40 text-destructive hover:bg-destructive/10 active:scale-95 transition-transform"
              title={t("cancel")}
            >
              <Trash2 style={{ height: 22, width: 22 }} />
            </button>
            <Button
              data-testid="voice-preview-send"
              onClick={sendPreview}
              className="h-12 px-8 rounded-xl"
            >
              <Send className="h-5 w-5 mr-2" /> {t("send")}
            </Button>
          </div>
        </div>
      ) : recLocked ? (
        /* ===== LOCKED RECORDING — 2 lines: timeline on top, tools below ===== */
        <div className="space-y-2">
          <div className="flex items-center gap-2 bg-destructive/10 border border-destructive/30 rounded-xl px-3 h-12">
            <span className="h-2.5 w-2.5 rounded-full bg-destructive recording-pulse" />
            <span className="text-sm font-medium text-destructive">{t("recording")} {Math.floor(recordSecs / 60)}:{String(recordSecs % 60).padStart(2, "0")}</span>
            <div className="flex-1" />
            <Lock className="h-4 w-4 text-destructive" data-testid="voice-locked-indicator" />
          </div>
          <div className="flex items-center justify-between px-1">
            <button
              type="button"
              data-testid="voice-cancel-btn"
              onClick={cancelRecording}
              className="h-12 w-12 flex items-center justify-center rounded-xl border border-destructive/40 text-destructive hover:bg-destructive/10 active:scale-95 transition-transform"
              title={t("cancel")}
            >
              <Trash2 style={{ height: 22, width: 22 }} />
            </button>
            <button
              type="button"
              data-testid="voice-stop-preview-btn"
              onClick={stopToPreview}
              className="h-12 w-12 flex items-center justify-center rounded-xl border-2 border-destructive text-destructive hover:bg-destructive/10 active:scale-95 transition-transform"
              title={t("listen_before_send")}
            >
              <Square style={{ height: 16, width: 16 }} className="fill-current" />
            </button>
            <Button
              data-testid="chat-composer-voice-send"
              onClick={reallyStopRecording}
              className="h-12 px-8 rounded-xl"
            >
              <Send className="h-5 w-5 mr-2" /> {t("send")}
            </Button>
          </div>
        </div>
      ) : (
      <div className="flex items-end gap-1.5">
        {!recording && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="chat-composer-plus-menu" className="h-12 w-12 shrink-0 text-primary">
              <Plus className="h-7 w-7" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" side="top">
            {plusItems.map((item) => (
              <DropdownMenuItem key={item.key} data-testid={`composer-menu-${item.key}`} onClick={item.action} className={`h-12 px-3 text-base ${item.danger ? "text-destructive focus:text-destructive" : ""}`}>
                <item.icon className="h-5 w-5 mr-2.5" /> {item.label}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        )}

        {/* 🏷️ Stickers (WhatsApp-style) */}
        {!recording && !editingMessage && !text.trim() && (
          <Button
            variant="ghost"
            size="icon"
            data-testid="chat-composer-stickers"
            onClick={() => setStickerOpen(true)}
            className="h-12 w-9 shrink-0 text-primary -ml-1.5"
            title={t("stickers")}
          >
            <StickerIcon className="h-5 w-5" />
          </Button>
        )}

        {/* ⚡ Quick replies — admin-managed 1-tap phrases */}
        {!recording && !editingMessage && !text.trim() && (
          <Button
            variant="ghost"
            size="icon"
            data-testid="chat-composer-quick-replies"
            onClick={openQuickReplies}
            className="h-12 w-9 shrink-0 text-primary -ml-1.5"
            title={t("quick_replies")}
          >
            <Zap className="h-5 w-5" />
          </Button>
        )}

        {recording ? (
          <div className="flex-1 flex items-center gap-2 bg-destructive/10 border border-destructive/30 rounded-xl px-3 h-12 overflow-hidden"
            data-testid="voice-recording-bar">
            <span className="h-2.5 w-2.5 rounded-full bg-destructive recording-pulse shrink-0" />
            <span className="text-sm font-medium text-destructive shrink-0">{t("recording")} {Math.floor(recordSecs / 60)}:{String(recordSecs % 60).padStart(2, "0")}</span>
            <div className="flex-1" />
            <span
              data-testid="voice-slide-cancel-hint"
              className="flex items-center gap-1 text-xs text-muted-foreground whitespace-nowrap"
              style={{ transform: `translateX(${-recSlideX * 60}px)`, opacity: 1 - recSlideX * 0.9, transition: recSlideX ? "none" : "transform .2s, opacity .2s" }}
            >
              <ChevronLeft className="h-4 w-4 animate-pulse" /> {t("slide_to_cancel")}
            </span>
          </div>
        ) : (
          <Textarea
            ref={inputRef}
            data-testid="chat-composer-input"
            value={text}
            onChange={onTextChange}
            onKeyDown={(e) => {
              if (e.key === "Escape" && mentionQuery !== null) {
                setMentionQuery(null);
                return;
              }
              // Enter = new line (never sends). Only the Send button sends.
              if (e.key === "Enter" && mentionQuery !== null && mentionMatches.length > 0) {
                e.preventDefault();
                pickMention(mentionMatches[0]);
              }
            }}
            placeholder={t("type_message")}
            rows={1}
            className="flex-1 resize-none min-h-[48px] max-h-32 rounded-xl text-base"
          />
        )}

        {text.trim() ? (
          <Button
            data-testid="chat-composer-send-button"
            onClick={editingMessage ? saveEdit : sendText}
            // Never steal focus from the textarea — tapping Send would blur it
            // and close the mobile keyboard (user must keep typing, WhatsApp-style)
            onPointerDown={(e) => e.preventDefault()}
            onMouseDown={(e) => e.preventDefault()}
            disabled={sending}
            size="icon"
            className="h-12 w-12 shrink-0 rounded-xl"
          >
            {editingMessage ? <Check className="h-6 w-6" /> : <Send className="h-6 w-6" />}
          </Button>
        ) : (
          <div className="relative shrink-0">
            {recording && (
              <div
                data-testid="voice-lock-hint"
                className="absolute -top-[78px] left-1/2 -translate-x-1/2 flex flex-col items-center gap-0.5 rounded-full border bg-card shadow-md px-2 py-2 animate-in fade-in slide-in-from-bottom-2 duration-200"
              >
                <Lock className="h-3.5 w-3.5 text-muted-foreground" />
                <ChevronUp className="h-4 w-4 text-primary animate-bounce" />
              </div>
            )}
            <Button
              data-testid="chat-composer-voice-hold"
              size="icon"
              variant={recording ? "destructive" : "default"}
              data-arming={recArming ? "1" : undefined}
              className={`h-12 w-12 rounded-xl no-select ${recording ? "scale-110" : ""} ${recArming ? "scale-110 ring-4 ring-amber-400/60 animate-pulse" : ""} transition-transform`}
              style={{ touchAction: "none" }}
              onMouseDown={startRecording}
              onMouseUp={stopRecording}
              onTouchStart={startRecording}
              onTouchMove={onRecordMove}
              onTouchEnd={stopRecording}
              onTouchCancel={stopRecording}
              title={t("hold_to_record")}
            >
              <Mic className="h-6 w-6" />
            </Button>
          </div>
        )}
      </div>
      )}

      {/* Dialogs — structured items inherit the active job topic when in a DM/crew chat */}
      <IssueDialog open={dialog === "issue"} onClose={() => setDialog(null)} conversation={conversation} jobId={conversation.job_id || linkedJob?.id} />
      <QuestionDialog open={dialog === "question"} onClose={() => setDialog(null)} conversation={conversation} extraMeta={linkMeta()} />
      <MeasurementDialog open={dialog === "measurement"} onClose={() => setDialog(null)} conversation={conversation} extraMeta={linkMeta()} />
      <MaterialRequestDialog open={dialog === "material"} onClose={() => setDialog(null)} conversation={conversation} extraMeta={linkMeta()} />
      <PollCreateDialog open={dialog === "poll-create"} onClose={() => setDialog(null)} conversation={conversation} extraMeta={linkMeta()} />
      <PhotoSendDialog
        open={dialog === "photo"}
        onClose={() => { setDialog(null); setPhotoDataUrl(null); setPhotoDataUrls(null); setPhotoAppend(null); setPhotoFromCamera(false); }}
        dataUrl={photoDataUrl}
        dataUrls={photoDataUrls}
        appendDataUrl={photoAppend}
        onTakeAnother={photoFromCamera ? () => fileRef.current?.click() : undefined}
        isJobConv={isJobConv || !!linkedJob}
        job={stampJob}
        onSend={sendPhoto}
      />
      <JobPickerDialog open={jobPickerOpen} onClose={() => setJobPickerOpen(false)} onPick={(job) => { setLinkedJob(job); setJobPickerOpen(false); }} />
      <StickerTray open={stickerOpen} onClose={() => setStickerOpen(false)} onSend={sendSticker} />
      <Dialog open={!!qualityAsk} onOpenChange={(v) => { if (!v) setQualityAsk(null); }}>
        <DialogContent className="max-w-xs" data-testid="video-quality-dialog">
          <DialogHeader><DialogTitle className="font-display text-base">{t("video_quality_title")}</DialogTitle></DialogHeader>
          <div className="grid gap-2">
            <Button onClick={() => sendNativeVideos(qualityAsk, "fast")} data-testid="video-quality-fast" className="h-12 justify-start">
              <Zap className="h-4 w-4 mr-2" /> <span className="text-left"><div className="font-semibold">{t("video_quality_fast")}</div><div className="text-[11px] opacity-80 font-normal">{t("video_quality_fast_hint")}</div></span>
            </Button>
            <Button variant="outline" onClick={() => sendNativeVideos(qualityAsk, "original")} data-testid="video-quality-original" className="h-12 justify-start">
              <Film className="h-4 w-4 mr-2" /> <span className="text-left"><div className="font-semibold">{t("video_quality_original")}</div><div className="text-[11px] opacity-70 font-normal">{t("video_quality_original_hint")}</div></span>
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Schedule message dialog */}
      <Dialog open={dialog === "schedule"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-sm" data-testid="schedule-message-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Clock className="h-5 w-5 text-primary" /> {t("schedule_message")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="rounded-lg bg-muted p-3 text-sm max-h-24 overflow-y-auto" data-testid="schedule-preview-text">{text.trim()}</div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">{t("schedule_when")}</label>
              <input
                type="datetime-local"
                value={schedDue}
                onChange={(e) => setSchedDue(e.target.value)}
                className="mt-1 w-full h-11 rounded-lg border bg-background px-3 text-sm"
                data-testid="schedule-datetime-input"
              />
            </div>
          </div>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button variant="outline" onClick={() => setDialog(null)} data-testid="schedule-cancel">{t("cancel")}</Button>
            <Button onClick={scheduleMessage} disabled={!text.trim() || !schedDue} data-testid="schedule-confirm">
              <Clock className="h-4 w-4 mr-1.5" /> {t("schedule_confirm")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ⚡ Quick replies — 1-tap send */}
      <Dialog open={quickOpen} onOpenChange={(o) => !o && setQuickOpen(false)}>
        <DialogContent className="max-w-sm" data-testid="quick-replies-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Zap className="h-5 w-5 text-primary" /> {t("quick_replies")}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-1.5 max-h-80 overflow-y-auto">
            {quickList === null && (
              <div className="space-y-1.5">
                {[1, 2, 3].map((i) => <div key={i} className="h-11 bg-muted rounded-xl animate-pulse" />)}
              </div>
            )}
            {quickList !== null && quickList.length === 0 && (
              <div className="text-center py-6">
                <p className="text-sm text-muted-foreground">{t("quick_replies_empty")}</p>
                <p className="text-xs text-muted-foreground mt-1">{t("quick_replies_user_hint")}</p>
              </div>
            )}
            {(quickList || []).map((qr) => (
              <button
                key={qr.id}
                type="button"
                data-testid={`quick-reply-item-${qr.id}`}
                onClick={() => sendQuickReply(qr.text)}
                className="w-full flex items-center gap-2.5 rounded-xl border bg-card hover:bg-primary/5 hover:border-primary/40 px-3.5 py-3 text-left text-sm font-medium transition-colors active:scale-[0.99]"
              >
                <Send className="h-4 w-4 text-primary shrink-0" />
                <span className="min-w-0 flex-1">{qr.text}</span>
                {qr.scope === "company" && (
                  <span className="text-[9px] uppercase tracking-wide text-muted-foreground border rounded-md px-1.5 py-0.5 shrink-0">
                    {t("phrase_company_badge")}
                  </span>
                )}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Pending scheduled messages list */}      <Dialog open={dialog === "scheduled-list"} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="max-w-sm" data-testid="scheduled-list-dialog">
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2"><Clock className="h-5 w-5 text-primary" /> {t("scheduled_list_title")}</DialogTitle>
          </DialogHeader>
          <div className="space-y-2 max-h-72 overflow-y-auto">
            {schedList.map((s) => (
              <div key={s.id} className="rounded-lg border p-3 flex items-start gap-2" data-testid="scheduled-item">
                <div className="flex-1 min-w-0">
                  <div className="text-sm line-clamp-2">{s.content}</div>
                  <div className="text-xs text-primary font-medium mt-1">
                    {new Date(s.due_at).toLocaleString(undefined, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                  </div>
                </div>
                <Button size="sm" variant="ghost" className="h-8 px-2 text-destructive shrink-0" onClick={() => cancelScheduled(s.id)} data-testid="scheduled-item-cancel">
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            ))}
            {schedList.length === 0 && <div className="text-sm text-muted-foreground text-center py-4">—</div>}
          </div>
        </DialogContent>
      </Dialog>

      {/* In-app video recorder + instant review player (Send button + frame markup + auto captions) */}
      <VideoRecorder open={recorderOpen} onClose={() => setRecorderOpen(false)} onRecorded={onRecordedVideo} />
      {recordedViewer && (
        <VideoViewer
          open
          media={recordedViewer.media}
          message={recordedViewer.message}
          conversation={conversation}
          markups={[]}
          onSend={sendRecordedVideo}
          expectCaptions={recordedViewer.expectCaptions !== false}
          onClose={discardRecordedVideo}
        />
      )}
    </div>
  );
}
